<template>
  <div class="tools-form">
    <h3 class="fs40">{{ tool.requestHeadline }}</h3>
    <div class="richtext-content pb-24" v-html="tool.requestText"></div>

    <b-row>
      <b-col md="6">
        <b-form @submit.prevent="onSubmit">
          <b-form-group :disabled="isCompleted">
            <FormItem
              v-if="tool.isExtended"
              class="mb-10"
              invalid-message="Please enter name"
              :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
              required
            >
              <b-form-input
                v-model="form.name"
                maxlength="100"
                :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                placeholder="Enter your name"
              />
            </FormItem>

            <FormItem
              class="mb-10"
              invalid-message="Please enter startup name"
              :state="$v.form.startup.$dirty ? !$v.form.startup.$error : null"
              required
            >
              <b-form-input
                v-model="form.startup"
                maxlength="100"
                :state="$v.form.startup.$dirty ? !$v.form.startup.$error : null"
                placeholder="Enter the name of your startup"
              />
            </FormItem>

            <FormItem
              v-if="tool.isExtended"
              class="mb-10"
              invalid-message="Please enter topic"
              :state="$v.form.topic.$dirty ? !$v.form.topic.$error : null"
              required
            >
              <b-form-input
                v-model="form.topic"
                maxlength="100"
                :state="$v.form.topic.$dirty ? !$v.form.topic.$error : null"
                placeholder="Which topic would you like to address"
              />
            </FormItem>

            <FormItem
              class="mb-10"
              invalid-message="Please enter email"
              :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
              required
            >
              <b-form-input
                v-model="form.email"
                :state="$v.form.email.$dirty ? !$v.form.email.$error : null"
                placeholder="Enter your email address"
              />
            </FormItem>

            <FormItem
              v-if="tool.isExtended"
              class="mb-16"
              invalid-message="Please enter phone number"
              :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
              required
            >
              <b-form-input
                v-model="form.phone"
                :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
                placeholder="Phone number"
              />
            </FormItem>

            <FormItem
              :state="$v.form.reCaptchaResponse.$dirty ? !$v.form.reCaptchaResponse.$error : null"
              invalid-message="Please verify you are not a robot"
            >
              <VueRecaptcha
                ref="reCaptcha"
                :sitekey="siteKey"
                :load-recaptcha-script="true"
                @verify="onVerify"
                @expired="onExpired"
              />
            </FormItem>
          </b-form-group>

          <b-button variant="primary" type="submit" :disabled="isCompleted">
            <span v-if="tool.isExtended">{{
              !isCompleted ? 'Send request' : 'Your request is sent!'
            }}</span>
            <span v-if="!tool.isExtended">{{
              !isCompleted ? 'Get the template' : 'We have sent the template to your email'
            }}</span>
            <img v-if="isCompleted" src="~/assets/img/icons/thumb-up.svg" alt="thumb up" />
          </b-button>

          <AlertSlideUpDown class="mt-10" :show="!!promptErrorMessage">{{
            promptErrorMessage
          }}</AlertSlideUpDown>
        </b-form>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import { validationMixin } from 'vuelidate';
import { required, minLength, email } from 'vuelidate/lib/validators';
import FormItem from '@/components/form/FormItem/index';
import AlertSlideUpDown from '@/components/AlertSlideUpDown';

export default {
  name: 'ToolsForm',
  components: {
    FormItem,
    VueRecaptcha,
    AlertSlideUpDown
  },
  mixins: [validationMixin],
  props: {
    tool: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      siteKey: process.env.GOOGLE_RECAPTCHA_SITE_KEY,
      promptErrorMessage: undefined,
      isRequesting: false,
      isCompleted: false,
      form: {
        name: undefined,
        startup: undefined,
        topic: undefined,
        email: undefined,
        phone: undefined,
        reCaptchaResponse: undefined
      }
    };
  },
  methods: {
    async onSubmit() {
      if (this.isRequesting || this.isCompleted) {
        return;
      }

      this.$v.form.$touch(); // will trigger errors to appear.
      // console.log(this.$v.form.$error)
      if (this.$v.form.$error) {
        return;
      }

      this.isRequesting = true;

      let res;
      try {
        res = await this.$axios.post(
          `/tools/${this.tool.key}/${this.tool.isExtended ? 'extended-form' : 'send-template'}`,
          this.form
        );

        if (this.tool.isDownloadable) {
          this.$gtm.push({
            event: 'ga.event',
            category: 'Tools',
            action: 'Tool Download'
          });
          this.$gtm.push({ event: 'fb', fbEvent: 'ToolDownload' });
        }

        this.$gtm.push({
          event: 'ga.event',
          category: 'form',
          action: this.tool.type + 'RequestSubmit'
        });

        this.$gtm.push({
          event: 'fb',
          fbEvent: 'trackCustom',
          fbParameters: this.tool.type + 'RequestSubmit'
        });

        this.$gtm.push({
          event: 'ga.event',
          category: 'tool',
          action: 'ToolFormSubmit'
        });

        this.$engagement({
          type: 'tool.formSubmit'
        });
      } catch (err) {
        this.promptErrorMessage = err.message;
      }

      this.isRequesting = false;
      if (res && res.status === 200) {
        this.isCompleted = true;
        this.promptErrorMessage = undefined;
        return;
      }

      if (this.$refs.reCaptcha) {
        this.$refs.reCaptcha.reset();
      }

      this.form.reCaptchaResponse = undefined;
    },
    onVerify(response) {
      this.form.reCaptchaResponse = response;
      this.$v.form.reCaptchaResponse.$touch();
    },
    onExpired() {
      this.form.reCaptchaResponse = undefined;
      this.$v.form.reCaptchaResponse.$touch();
    }
  },
  validations() {
    if (this.tool.isExtended) {
      return {
        form: {
          name: {
            required
          },
          startup: {
            required
          },
          topic: {
            required
          },
          email: {
            required,
            email
          },
          phone: {
            required,
            minLength: minLength(8)
          },
          reCaptchaResponse: {
            required
          }
        }
      };
    } else {
      return {
        form: {
          startup: {
            required
          },
          email: {
            required,
            email
          },
          reCaptchaResponse: {
            required
          }
        }
      };
    }
  }
};
</script>

<style lang="scss" scoped>
.tools-form {
  .form-group {
    margin-bottom: 0;
  }

  .btn {
    img {
      height: rem(24px);
      width: auto;
      margin: rem(-6px) 0 0 rem(16px);
    }

    &:disabled,
    &.disabled {
      border: 1px solid #c9c7c7;
      background: #fff;
      color: $gray-900;
      font-weight: 400;
    }
  }
}
</style>
